import { useTranslate } from '@athena/ui'

import type { ImageSize } from './useAutomaticPractice.js'

/**
 * TEMPORARY: what the upload weighs, before and after compression.
 *
 * A diagnostic, not a feature. It exists so the compression settings in
 * `extraction/compressImage.ts` — 1600px longest edge, JPEG at 0.82 — can be
 * judged against real photographs rather than assumed: a page that still
 * arrives at two megabytes means the settings are wrong for the cameras
 * tutors actually use, and that is invisible without a number.
 *
 * ── Removing it ──────────────────────────────────────────────────────────
 *
 * Delete this file, the `sizes` field on `AutomaticPractice`, the
 * `compressedBytes` field on `PageState`, and the `practice.automatic.sizes.*`
 * dictionary keys. Nothing else reads any of them.
 *
 * It is translated despite being temporary, because a tutor can see it: the
 * dashed border says "diagnostic" to a developer, and says nothing at all to
 * someone who does not know it is one. A rule with an exception for things
 * that felt short-lived is not a rule.
 */
export interface ImageSizeReportProps {
  sizes: readonly ImageSize[]
}

/**
 * Bytes as a person reads them.
 *
 * Local rather than in `lib/format.ts` because it arrived with this panel and
 * leaves with it — putting it in the shared formatter would leave a function
 * behind that nothing calls. Deliberately not `Intl`-localized: these are
 * diagnostic figures, and the unit is the same word in both languages.
 */
function bytes(value: number): string {
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`
  return `${(value / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * The change in size, signed and as a whole percent.
 *
 * Signed because re-encoding does not always shrink: a small PNG of a diagram
 * can come out larger as a JPEG, and a panel that rendered that as a negative
 * saving would print "−-15%". A growth is exactly the thing someone tuning
 * `compressImage.ts` needs to see, so it is shown as `+15%` rather than hidden.
 */
function delta(original: number, compressed: number): string {
  if (original <= 0) return ''
  const percent = Math.round((1 - compressed / original) * 100)
  return percent >= 0 ? ` (−${percent}%)` : ` (+${-percent}%)`
}

export function ImageSizeReport({ sizes }: ImageSizeReportProps) {
  const t = useTranslate()

  if (sizes.length === 0) return null

  const prepared = sizes.filter(
    (size): size is ImageSize & { compressedBytes: number } => size.compressedBytes !== null,
  )
  const originalTotal = sizes.reduce((sum, size) => sum + size.originalBytes, 0)
  const compressedTotal = prepared.reduce((sum, size) => sum + size.compressedBytes, 0)

  return (
    <div className="flex flex-col gap-2 rounded-md border border-dashed border-border-strong bg-surface-secondary p-4">
      <h3 className="text-body-sm font-semibold text-text-secondary">
        {t('practice.automatic.sizes.title')}
      </h3>

      {/* A table because it is one: a row per image, two comparable figures
          per row. `tabular-nums` so the columns line up as they change. */}
      <table className="w-full text-body-sm tabular-nums text-text-secondary">
        <thead>
          <tr className="text-left">
            <th scope="col" className="font-normal">
              {t('practice.automatic.sizes.image')}
            </th>
            <th scope="col" className="font-normal">
              {t('practice.automatic.sizes.uploaded')}
            </th>
            <th scope="col" className="font-normal">
              {t('practice.automatic.sizes.sent')}
            </th>
          </tr>
        </thead>
        <tbody>
          {sizes.map((size) => (
            <tr key={size.id}>
              <td className="max-w-40 truncate pr-4">{size.name}</td>
              <td className="pr-4">{bytes(size.originalBytes)}</td>
              <td>
                {size.compressedBytes === null
                  ? // Not compressed yet. A dash rather than a zero, which
                    // would read as "compressed to nothing".
                    '—'
                  : `${bytes(size.compressedBytes)}${delta(size.originalBytes, size.compressedBytes)}`}
              </td>
            </tr>
          ))}
        </tbody>
        {/* The total is the number that matters: it is what the request
            carries, and what the server's per-request ceiling is about. */}
        {prepared.length === sizes.length && sizes.length > 1 ? (
          <tfoot>
            <tr className="font-semibold text-text">
              <td className="pr-4">{t('practice.automatic.sizes.total')}</td>
              <td className="pr-4">{bytes(originalTotal)}</td>
              <td>{`${bytes(compressedTotal)}${delta(originalTotal, compressedTotal)}`}</td>
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  )
}
